import React, { useState } from 'react';
import { Menu, X, User, Bell } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { MENTEE_NAVIGATION } from './ui/mentorshipData';
import NotificationCenter from './NotificationCenter';

const MentorshipHeader = ({ 
  isAuthenticated = false, 
  user = null, 
  onSignUp = () => {}, 
  onLogin = () => {} 
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleNavigation = (path) => {
    navigate(path);
    setIsMenuOpen(false);
  };

  const handleLogoClick = () => {
    // Always go to homepage, but user stays logged in
    navigate('/');
    setIsMenuOpen(false);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-black backdrop-blur-md border-b border-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex items-center cursor-pointer" onClick={handleLogoClick}>
            <img 
              src="https://customer-assets.emergentagent.com/job_07e03d84-96d6-4787-9cf6-a4abd4f4b8fa/artifacts/9o24dy6c_logo.PNG"
              alt="OA Ministries Logo"
              className="h-12 w-auto"
            />
            <div className="text-2xl font-light text-white tracking-tight hidden sm:block ml-4">
              Obed Ansu
              <div className="text-xs font-medium text-gray-300 uppercase tracking-wider">
                Academy
              </div>
            </div>
          </div>

          {/* Desktop Navigation - Unauthenticated */}
          {!isAuthenticated && (
            <nav className="hidden md:flex items-center space-x-8">
              <button 
                onClick={() => handleNavigation('/about')}
                className="text-gray-300 hover:text-white transition-colors duration-200 font-medium"
              >
                About
              </button>
              <button 
                onClick={() => handleNavigation('/mentorship-programs')}
                className="text-gray-300 hover:text-white transition-colors duration-200 font-medium"
              >
                Mentorship Programs
              </button>
              <button 
                onClick={() => handleNavigation('/give')}
                className="text-gray-300 hover:text-white transition-colors duration-200 font-medium"
              >
                Give
              </button>
              <button 
                onClick={() => handleNavigation('/contact')}
                className="text-gray-300 hover:text-white transition-colors duration-200 font-medium"
              >
                Contact
              </button>
              <button 
                onClick={() => handleNavigation('/download')}
                className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-full transition-colors duration-200 font-medium flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Get App
              </button>
            </nav>
          )}

          {/* Desktop Navigation - Authenticated */}
          {isAuthenticated && (
            <nav className="hidden md:flex items-center space-x-6">
              <button
                onClick={() => handleNavigation('/dashboard')}
                className="text-gray-300 hover:text-white transition-colors duration-200 font-medium text-sm"
              >
                Dashboard
              </button>
              <button
                onClick={() => handleNavigation('/courses')}
                className="text-gray-300 hover:text-white transition-colors duration-200 font-medium text-sm"
              >
                My Courses
              </button>
              
              {/* Only show Appointments for mentorship members, mentors, or admins */}
              {user && (
                user.membership_tier === 'mentorship' || 
                user.role === 'mentor' || 
                user.role === 'admin' ||
                user.is_admin ||
                ['obedansu1994@gmail.com', 'admin@obedansuacademy.uk', 'obed@obedansuacademy.uk', 'mentorship@obedansuministries.com'].includes(user.email?.toLowerCase())
              ) && (
                <button
                  onClick={() => handleNavigation('/appointments')}
                  className="text-gray-300 hover:text-white transition-colors duration-200 font-medium text-sm"
                >
                  Appointments
                </button>
              )}
              
              <button
                onClick={() => handleNavigation('/library')}
                className="text-gray-300 hover:text-white transition-colors duration-200 font-medium text-sm"
              >
                Library
              </button>
              <button
                onClick={() => handleNavigation('/community')}
                className="text-gray-300 hover:text-white transition-colors duration-200 font-medium text-sm"
              >
                Community
              </button>

              {/* CODEX — opens the biblical discovery game in a new tab */}
              <a
                href="/codex/dashboard"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="codex-nav-button"
                title="CODEX: The Ancient Scrolls — opens in new tab"
                className="ml-1 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-bold text-amber-200 bg-gradient-to-r from-purple-900/60 to-amber-900/30 border border-amber-500/40 hover:border-amber-400 hover:text-amber-100 hover:shadow-[0_0_18px_rgba(245,158,11,0.35)] transition-all duration-200"
                style={{ fontFamily: "'Cinzel', serif", letterSpacing: '0.18em' }}
              >
                <span aria-hidden>📜</span>
                CODEX
              </a>
              
              {/* ADMIN PANEL - Show for admins by role or email */}
              {user && (user.role === 'admin' || user.is_admin || ['obedansu1994@gmail.com', 'admin@obedansuacademy.uk', 'obed@obedansuacademy.uk', 'mentorship@obedansuministries.com'].includes(user.email?.toLowerCase())) && (
                <button
                  onClick={() => handleNavigation('/control-center')}
                  className="text-orange-400 hover:text-orange-300 transition-colors duration-200 font-bold text-sm border-2 border-orange-500 px-4 py-1.5 rounded-lg bg-orange-500/10"
                >
                  CONTROL CENTER
                </button>
              )}
              
              {/* Mentor Portal - Show for mentors/admins by role or email - Now also goes to Control Center */}
              {user && (user.role === 'admin' || user.role === 'mentor' || user.is_admin || user.is_mentor || ['obedansu1994@gmail.com', 'admin@obedansuacademy.uk', 'obed@obedansuacademy.uk', 'mentorship@obedansuministries.com'].includes(user.email?.toLowerCase())) && (
                <button
                  onClick={() => handleNavigation('/control-center')}
                  className="text-purple-400 hover:text-purple-300 transition-colors duration-200 font-semibold text-sm border border-purple-500/30 px-3 py-1.5 rounded-lg"
                >
                  Mentor Portal
                </button>
              )}
            </nav>
          )}

          {/* Right Side Actions */}
          <div className="flex items-center space-x-4">
            {!isAuthenticated ? (
              /* Unauthenticated Actions */
              <div className="hidden md:flex items-center space-x-4">
                <button 
                  onClick={onLogin}
                  className="text-gray-300 hover:text-white transition-colors duration-200 font-medium"
                >
                  Log In
                </button>
                <button 
                  onClick={onSignUp}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded-full font-medium transition-colors duration-200"
                >
                  Join Now
                </button>
              </div>
            ) : (
              /* Authenticated Actions */
              <div className="hidden md:flex items-center space-x-4">
                {/* Notifications */}
                <NotificationCenter />

                {/* Profile Dropdown */}
                <div className="relative">
                  <button
                    onClick={() => setIsProfileOpen(!isProfileOpen)}
                    className="flex items-center space-x-2 text-gray-300 hover:text-white transition-colors duration-200"
                  >
                    <div className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center">
                      <User className="w-5 h-5 text-white" />
                    </div>
                    <span className="font-medium text-sm">
                      {user?.role === 'admin' || user?.is_admin ? 'Admin' : 
                       user?.role === 'mentor' || user?.is_mentor ? 'Mentor' : 
                       user?.profile?.first_name || 'Mentee'}
                    </span>
                  </button>

                  {isProfileOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-2 border border-gray-200">
                      <button 
                        onClick={() => {
                          handleNavigation('/profile');
                          setIsProfileOpen(false);
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        Profile
                      </button>
                      {/* Only show My Assignments for regular mentees, not mentors/admins */}
                      {user && !(
                        user.role === 'mentor' || 
                        user.role === 'admin' || 
                        user.is_admin || 
                        user.is_mentor ||
                        ['obedansu1994@gmail.com', 'mentorship@obedansuministries.com', 'admin@obedansuacademy.uk', 'obed@obedansuacademy.uk'].includes(user.email?.toLowerCase())
                      ) && (
                        <button 
                          onClick={() => {
                            handleNavigation('/assignments');
                            setIsProfileOpen(false);
                          }}
                          className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          My Assignments
                        </button>
                      )}
                      <button 
                        onClick={() => {
                          handleNavigation('/payments');
                          setIsProfileOpen(false);
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        Payments
                      </button>
                      <button 
                        onClick={() => {
                          handleNavigation('/settings');
                          setIsProfileOpen(false);
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        Settings
                      </button>
                      <hr className="my-2 border-gray-200" />
                      <button 
                        onClick={() => {
                          localStorage.removeItem('access_token');
                          localStorage.removeItem('user_data');
                          window.location.href = '/';
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                      >
                        Sign Out
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 text-gray-300 hover:text-white transition-colors duration-200"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 bg-black border-t border-gray-800">
              {!isAuthenticated ? (
                /* Unauthenticated Mobile Menu */
                <>
                  <button 
                    onClick={() => handleNavigation('/about')}
                    className="block w-full text-left px-3 py-2 text-gray-300 hover:text-white transition-colors duration-200"
                  >
                    About
                  </button>
                  <button 
                    onClick={() => handleNavigation('/mentorship-programs')}
                    className="block w-full text-left px-3 py-2 text-gray-300 hover:text-white transition-colors duration-200"
                  >
                    Mentorship Programs
                  </button>
                  <button 
                    onClick={() => handleNavigation('/give')}
                    className="block w-full text-left px-3 py-2 text-gray-300 hover:text-white transition-colors duration-200"
                  >
                    Give
                  </button>
                  <button 
                    onClick={() => handleNavigation('/contact')}
                    className="block w-full text-left px-3 py-2 text-gray-300 hover:text-white transition-colors duration-200"
                  >
                    Contact
                  </button>
                  <button 
                    onClick={() => handleNavigation('/download')}
                    className="flex items-center gap-2 w-full text-left px-3 py-2 text-purple-400 hover:text-purple-300 transition-colors duration-200"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Get App
                  </button>
                  <div className="pt-4 space-y-2">
                    <button 
                      onClick={onLogin}
                      className="block w-full text-left px-3 py-2 text-gray-300 hover:text-white transition-colors duration-200"
                    >
                      Log In
                    </button>
                    <button 
                      onClick={onSignUp}
                      className="block w-full bg-purple-600 text-white px-3 py-2 rounded-lg font-medium hover:bg-purple-700 transition-colors duration-200"
                    >
                      Join Now
                    </button>
                  </div>
                </>
              ) : (
                /* Authenticated Mobile Menu */
                <>
                  <button 
                    onClick={() => handleNavigation('/dashboard')}
                    className="block w-full text-left px-3 py-2 text-gray-300 hover:text-white transition-colors duration-200"
                  >
                    Dashboard
                  </button>
                  <button 
                    onClick={() => handleNavigation('/courses')}
                    className="block w-full text-left px-3 py-2 text-gray-300 hover:text-white transition-colors duration-200"
                  >
                    My Courses
                  </button>
                  
                  {/* Only show Appointments for mentorship members, mentors, or admins */}
                  {user && (
                    user.membership_tier === 'mentorship' || 
                    user.role === 'mentor' || 
                    user.role === 'admin' ||
                    ['obedansu1994@gmail.com', 'admin@obedansuacademy.uk', 'obed@obedansuacademy.uk', 'mentorship@obedansuministries.com'].includes(user.email?.toLowerCase())
                  ) && (
                    <button 
                      onClick={() => handleNavigation('/appointments')}
                      className="block w-full text-left px-3 py-2 text-gray-300 hover:text-white transition-colors duration-200"
                    >
                      Appointments
                    </button>
                  )}
                  
                  <button 
                    onClick={() => handleNavigation('/library')}
                    className="block w-full text-left px-3 py-2 text-gray-300 hover:text-white transition-colors duration-200"
                  >
                    Library
                  </button>
                  <button 
                    onClick={() => handleNavigation('/community')}
                    className="block w-full text-left px-3 py-2 text-gray-300 hover:text-white transition-colors duration-200"
                  >
                    Community
                  </button>

                  {/* CODEX - Mobile */}
                  <a
                    href="/codex/dashboard"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setIsMenuOpen(false)}
                    data-testid="codex-nav-button-mobile"
                    className="block w-full text-left px-3 py-2 text-amber-300 hover:text-amber-200 transition-colors duration-200 font-bold"
                    style={{ fontFamily: "'Cinzel', serif", letterSpacing: '0.18em' }}
                  >
                    📜 CODEX (NEW TAB)
                  </a>
                  
                  {/* Admin Panel - Mobile */}
                  {user && (user.role === 'admin' || user.is_admin || ['obedansu1994@gmail.com', 'admin@obedansuacademy.uk', 'obed@obedansuacademy.uk', 'mentorship@obedansuministries.com'].includes(user.email?.toLowerCase())) && (
                    <button 
                      onClick={() => handleNavigation('/admin/content')}
                      className="block w-full text-left px-3 py-2 text-orange-400 hover:text-orange-300 transition-colors duration-200 font-bold"
                    >
                      ⚙️ ADMIN PANEL
                    </button>
                  )}
                  
                  {/* Mentor Portal - Mobile */}
                  {user && (user.role === 'admin' || user.role === 'mentor' || user.is_admin || user.is_mentor || ['obedansu1994@gmail.com', 'admin@obedansuacademy.uk', 'obed@obedansuacademy.uk', 'mentorship@obedansuministries.com'].includes(user.email?.toLowerCase())) && (
                    <button 
                      onClick={() => handleNavigation('/mentor')}
                      className="block w-full text-left px-3 py-2 text-purple-400 hover:text-purple-300 transition-colors duration-200 font-semibold"
                    >
                      👥 Mentor Portal
                    </button>
                  )}
                  
                  <div className="pt-4 border-t border-gray-800">
                    <button 
                      onClick={() => {
                        localStorage.removeItem('access_token');
                        localStorage.removeItem('user_data');
                        window.location.href = '/';
                      }}
                      className="block w-full text-left px-3 py-2 text-red-400 hover:text-red-300 transition-colors duration-200"
                    >
                      Sign Out
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default MentorshipHeader;